import {
  StringSetting,
  useUserSettingWithDefault,
} from "shared-ts-utils-user-settings";
import i18next from "i18next";
import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import {
  normalizeInitialUserFacingLanguage,
  useI18Next,
  userFacingToI18nLng,
} from "shared-ts-utils-i18n";

export type UseLanguageUserSettingReturnType = [
  string,
  Dispatch<SetStateAction<string>>
];

export const useLanguageUserSetting = (
  defaultLanguage?: string
): UseLanguageUserSettingReturnType => {
  const defaultValue = defaultLanguage ?? i18next.language ?? "en";
  const [value, setValue] = useUserSettingWithDefault(
    "language",
    StringSetting,
    defaultValue,
    {
      storeCopyToAppLocally: true,
    }
  );

  // Corruption-resistance guard: a stored value that is not in the current
  // user-facing locale set (read from `useI18Next().languages`, the same
  // source `LanguageProperty` uses, so the guard cannot drift from the
  // dropdown) is treated as if absent. We return the default, write the
  // default back so the next read is clean, and emit a single
  // `console.warn` per distinct corrupted value naming the unknown value.
  // If we are mounted without an `I18NextProvider` above us (e.g. in unit
  // tests), we have no known locale set and skip the guard rather than
  // coerce everything to the default. See PRD i18n-language-mapping.
  const { languages } = useI18Next();
  const warnedFor = useRef<Set<string>>(new Set());
  const normalizedValue = normalizeInitialUserFacingLanguage(
    value,
    defaultValue
  );
  const isLegacyI18nBasename =
    value != null &&
    Array.isArray(languages) &&
    languages.length > 0 &&
    !languages.includes(value) &&
    languages.includes(normalizedValue) &&
    userFacingToI18nLng[normalizedValue] === value;
  const isCorrupted =
    value != null &&
    Array.isArray(languages) &&
    languages.length > 0 &&
    !languages.includes(value) &&
    !isLegacyI18nBasename;

  useEffect(() => {
    if (!isLegacyI18nBasename) return;
    setValue(normalizedValue);
  }, [isLegacyI18nBasename, normalizedValue, setValue]);

  useEffect(() => {
    if (!isCorrupted) return;
    if (!warnedFor.current.has(value)) {
      warnedFor.current.add(value);
      console.warn(
        `useLanguageUserSetting: stored language "${value}" is not in the ` +
          `current user-facing locale set [${languages.join(", ")}]; ` +
          `falling back to default "${defaultValue}".`
      );
    }
    setValue(defaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCorrupted, value]);

  if (isCorrupted) {
    return [defaultValue, setValue];
  }
  if (isLegacyI18nBasename) {
    return [normalizedValue, setValue];
  }
  return [value, setValue];
};
